import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchAccountTypesRequest,
  fetchOnboardingFieldsRequest,
  setProfileInfoRequest,
  verifyGstRequest,
  verifyPanRequest,
  type VerifyGstPayload,
  type VerifyPanPayload,
} from '@/features/auth/api/authApi';
import { getErrorMessage } from '@/shared/api/errors';
import { queryKeys } from '@/shared/lib/query/queryKeys';

export function   useOnboardingFieldsQuery(roleKey?: string | null) {
  return useQuery({
    queryKey: [...queryKeys.auth.root, 'onboarding-fields', roleKey ?? ''] as const,
    queryFn: () => fetchOnboardingFieldsRequest(roleKey!),
    enabled: Boolean(roleKey),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useSetProfileInfoMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      setProfileInfoRequest(payload),
    onSuccess: () => {
      toast.success('Profile saved');
      navigate('/create-password');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to save profile'));
    },
  });
}

export function useVerifyPanMutation() {
  return useMutation({
    mutationFn: (payload: VerifyPanPayload) => verifyPanRequest(payload),
    onError: (error) => {
      toast.error(getErrorMessage(error, 'PAN verification failed'));
    },
  });
}

export function useVerifyGstMutation() {
  return useMutation({
    mutationFn: (payload: VerifyGstPayload) => verifyGstRequest(payload),
    onError: (error) => {
      toast.error(getErrorMessage(error, 'GST verification failed'));
    },
  });
}

export function useAccountTypesQuery() {
  return useQuery({
    queryKey: [...queryKeys.auth.root, 'account-types'] as const,
    queryFn: fetchAccountTypesRequest,
    staleTime: 5 * 60 * 1000,
  });
}
